<template>
    <div class="rf-view">
        <NavigationBar :title="$t('settings.title')">
            <BackButton v-if="canPop" slot="left" @click="pop" />
        </NavigationBar>
        <main>
            <h1>
                {{ $t('settings.title') }}
            </h1>

            <List>
                <ListItem :selectable="true" @click="openOrderHistory">
                    {{ $t('settings.orderHistory.title') }}

                    <span slot="right" class="icon arrow-right gray" />
                </ListItem>
                <ListItem :selectable="true" @click="openNotificationSettings">
                    {{ $t('settings.notifications.title') }}

                    <span slot="right" class="icon arrow-right gray" />
                </ListItem>
                <ListItem :selectable="true" @click="openLanguageSelector">
                    {{ $t('settings.language.title') }}

                    <span slot="right" class="icon arrow-right gray" />
                </ListItem>

                <ListItem :selectable="true" @click="openProfileInformationEdit">
                    {{ $t('settings.profileInformation.title') }}

                    <span slot="right" class="icon arrow-right gray" />
                </ListItem>
                <ListItem :selectable="true" @click="openProfileSelector">
                    {{ $t('settings.profiles.title') }}

                    <span slot="right" class="icon arrow-right gray" />
                </ListItem>
                <ListItem :selectable="true" @click="logout">
                    {{ $t('buttons.logout') }}

                    <span slot="right" class="icon arrow-right gray" />
                </ListItem>
            </List>

            <p v-if="appVersion" class="style-description app-version" @click="onVersionTap">
                v{{ appVersion }}
            </p>
        </main>
    </div>
</template>


<script lang="ts">
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { BackButton, List, ListItem, NavigationBar } from "@restofrit/components";
// TEMPORARY: RapidTapGate dropped from this import while the switcher is
// disabled for the hardcoded test build — restore it with the tapGate field.
import { Session } from "@restofrit/networking";
import { Customer } from "@restofrit/structures";
import { ComponentWithProperties, NavigationMixin } from "@simonbackx/vue-app-navigation";
import { Component, Mixins, Prop } from "vue-property-decorator";

import LanguageSelectionView from "./LanguageSelectionView.vue";
import NotificationSettingsView from "./NotificationSettingsView.vue";
import ProfileSelectionView from "./ProfileSelectionView.vue";
import ProfileInformationEditView from "./ProfileInformationEditView.vue";
import OrderHistoryView from "./OrderHistoryView.vue";
// TEMPORARY: unused while the switcher is disabled. DeveloperSettingsView.vue
// itself is intentionally left in the tree — restore this import with it.
// import DeveloperSettingsView from "./DeveloperSettingsView.vue";

@Component({
    components: {
        NavigationBar,
        List, 
        ListItem,
        BackButton
    },
})
export default class SettingsView extends Mixins(NavigationMixin) {
    @Prop({ required: true })
    customer: Customer

    // Hidden developer menu: the switcher ships in every build (one binary goes
    // all the way to the store), so it must NOT be a visible row. RapidTapGate
    // on the small app-version line (not the page title/7 taps — that's the
    // well-known Android developer-options trope) debounces against idle/
    // accidental taps; admission itself is gated by customer.settings.isTester,
    // a flag the SERVER controls (already used to gate push delivery outside
    // production — see Customer.ts) and the client only reads, never computes.
    // Unlike a local password, there's no secret in the bundle to find by
    // reading the JS — a non-tester tapping this gets silent no-op, not an
    // "access denied" (which would itself confirm the mechanism exists).
    //
    // TEMPORARY — DISABLED for the hardcoded test build (see CLAUDE.md
    // "Hardcoded test build"). That build talks to the Azure test API
    // directly via API_URL, so there is nothing to switch between and the
    // switcher would call /session/environment on a deployment that never
    // proxies anywhere. Uncomment `tapGate` and the body of onVersionTap()
    // below — plus the RapidTapGate / DeveloperSettingsView imports at the
    // top of this file — to restore it for the switcher build.
    appVersion = ""
    // tapGate = new RapidTapGate()

    async mounted() {
        if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
            try {
                const info = await CapApp.getInfo()
                this.appVersion = info.version
            } catch (e) {
                // ignore — footer just won't render without a version
            }
        }
    }

    onVersionTap() {
        // TEMPORARY — no-op in the hardcoded test build. The template still
        // binds @click here so the version line behaves identically (an inert
        // tap), which keeps this diff to one file's logic rather than markup.
        // Restore with:
        // if (this.tapGate.tap() && this.customer.settings.isTester) {
        //     this.show(new ComponentWithProperties(DeveloperSettingsView, {}))
        // }
    }

    openOrderHistory() {
        this.show(new ComponentWithProperties(OrderHistoryView, { initialCustomer: this.customer }))
    }

    openProfileInformationEdit() {
        this.show(new ComponentWithProperties(ProfileInformationEditView, { customer: this.customer }))
    }

    openProfileSelector() {
        this.show(new ComponentWithProperties(ProfileSelectionView, { initialCustomer: this.customer }))
    }

    openLanguageSelector() {
        this.show(new ComponentWithProperties(LanguageSelectionView, { customer: this.customer }))
    }

    openNotificationSettings() {
        this.show(new ComponentWithProperties(NotificationSettingsView, { customer: this.customer }))
    }

    logout() {
        if (!confirm(this.$t('settings.logout.confirmMessage').toString())) {
            return;
        }
        Session.shared.logout()
    }

}
</script>

<style lang="scss" scoped>
.app-version {
    text-align: center;
    padding: 20px 0 10px;
    touch-action: manipulation;
    user-select: none;
}
</style>